import React from "react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Warehouse, Boxes, Layers, PackageCheck, ShieldCheck, MapPinned, MonitorSmartphone } from "lucide-react";
import { IMAGES } from "../lib/site";
import { Reveal } from "../components/Reveal";
import { PageHero, SectionHeader, CTAButton, useSeo, Eyebrow } from "../components/Shared";
import { ClientPortalLink } from "../components/ClientPortalLink";
import { Marquee } from "../components/Marquee";

const SECTIONS = [
  { icon: Warehouse, t: "One Managed Home for Your Brand Assets", d: "Keep marketing materials, apparel, promotional products, and exhibit assets secure and organized in one managed location — so the right items are always accounted for and ready to move." },
  { icon: Boxes, t: "Inventory Management", d: "Receiving, item tracking, inventory visibility, reorder awareness, and organized storage keep your program running without guesswork." },
  { icon: Layers, t: "Kitting and Assembly", d: "Event kits, new-hire kits, sales kits, recruiting packages, and custom combinations of materials — assembled and staged the way you need them." },
  { icon: PackageCheck, t: "Pick, Pack, and Fulfill", d: "Accurate order preparation, packaging, shipment coordination, and distribution to individuals, offices, or event locations." },
  { icon: ShieldCheck, t: "Inspection and Readiness", d: "We inspect outgoing assets before shipment and returned items before putting them back into inventory, so what ships is what you expect." },
  { icon: MapPinned, t: "Event and Multi-Location Distribution", d: "Prepare materials for a single event or coordinate repeat shipments across a larger, multi-location program." },
  { icon: MonitorSmartphone, t: "Cloud-Based Access", d: "Authorized ordering, requests, visibility, and optional approvals through the client portal — for the teams that need it." },
];

const FLOW = ["Receive", "Organize", "Order", "Pick & Pack", "Ship", "Inspect Returns", "Update Inventory"];

const FAQS = [
  { q: "What types of items can MJG store and fulfill?", a: "MJG can store and fulfill a wide range of branded assets, including apparel, promotional products, marketing collateral, and exhibit materials. Contact MJG to discuss the requirements of your program." },
  { q: "Can MJG assemble custom kits?", a: "Yes. We assemble event kits, new-hire kits, sales kits, recruiting packages, and other custom combinations of materials based on your needs." },
  { q: "Can different departments or locations submit requests?", a: "Authorized team members can submit orders and requests, with optional approval workflows. Contact MJG to discuss the requirements of your program." },
  { q: "Can MJG support event-specific shipments?", a: "Yes. We prepare and coordinate materials for single events and can support repeat shipments across multi-location programs." },
  { q: "How do authorized users access inventory?", a: "Authorized users access inventory, ordering, and requests through the cloud-based client portal. Contact MJG to discuss access for your team." },
];

export default function Fulfillment() {
  useSeo(
    "Fulfillment & Warehousing for Branded Assets | MJG Promotions",
    "Fulfillment and warehousing in Fairfield, NJ. Inventory management, kitting, pick-and-pack, inspection, and cloud-based ordering for branded assets and events."
  );
  return (
    <>
      <PageHero
        eyebrow="Fulfillment & Warehousing"
        title="Fulfillment and Warehousing for Branded Assets"
        intro="Keep marketing materials, apparel, promotional products, and exhibit assets organized, visible, and ready for the people and events that need them. MJG combines hands-on warehouse support with streamlined ordering and fulfillment."
        image={IMAGES.aisleSilhouette}
      />

      {/* Sections */}
      <section className="bg-white py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5">
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-4">
              <div className="lg:sticky lg:top-28">
                <SectionHeader eyebrow="Operational Depth" title="Hands-On Warehouse Support, End to End" body="Our fulfillment offering is the core of what we do. Here's how we keep every branded asset organized and event-ready." />
                <img src={IMAGES.packStation} alt="MJG staff preparing shipments at a packing station" className="mt-8 aspect-[4/5] w-full rounded-sm object-cover" loading="lazy" />
                <div className="mt-6 flex flex-col gap-3">
                  <CTAButton to="/contact" testId="ful-primary-cta" variant="primary">Discuss Your Fulfillment Program</CTAButton>
                  <ClientPortalLink testId="ful-portal-cta" showIcon className="inline-flex items-center justify-center border border-brand-black/20 px-6 py-3.5 text-sm font-bold text-brand-black transition-colors hover:border-brand-red hover:text-brand-red">
                    Access Client Portal
                  </ClientPortalLink>
                </div>
              </div>
            </div>

            <div className="lg:col-span-8">
              <div className="grid gap-px overflow-hidden border border-brand-line bg-brand-line sm:grid-cols-2">
                {SECTIONS.map((s, i) => (
                  <Reveal key={s.t} delay={(i % 2) * 0.06}>
                    <div className="group h-full bg-white p-7 transition-colors hover:bg-brand-warm">
                      <s.icon className="h-7 w-7 text-brand-red" aria-hidden="true" />
                      <h3 className="mt-5 text-lg font-bold text-brand-black">{s.t}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-brand-gray">{s.d}</p>
                    </div>
                  </Reveal>
                ))}
                <div className="hidden bg-brand-black p-7 sm:block">
                  <p className="font-mono text-xs uppercase tracking-widest text-brand-red">MJG</p>
                  <p className="mt-4 text-lg font-bold leading-snug text-white">From the warehouse to the event floor — ready when and where you need it.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works flow */}
      <section className="bg-brand-black py-20 sm:py-28" data-testid="how-it-works">
        <div className="mx-auto max-w-7xl px-5">
          <SectionHeader dark eyebrow="How It Works" title="A Clear Path for Every Asset" />
          <div className="mt-12 flex flex-wrap items-stretch gap-3">
            {FLOW.map((step, i) => (
              <Reveal key={step} delay={i * 0.05} className="flex-1 basis-[calc(50%-0.75rem)] sm:basis-[calc(25%-0.75rem)]">
                <div className="flex h-full flex-col justify-between border border-white/15 p-5">
                  <span className="font-mono text-xs text-brand-red">{String(i + 1).padStart(2, "0")}</span>
                  <span className="mt-6 text-base font-bold text-white">{step}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Marquee />

      {/* FAQ */}
      <section className="bg-white py-20 sm:py-28" data-testid="ful-faq">
        <div className="mx-auto max-w-3xl px-5">
          <div className="text-center">
            <Eyebrow className="justify-center">FAQ</Eyebrow>
            <h2 className="mt-4 text-2xl font-extrabold tracking-tight text-brand-black sm:text-3xl lg:text-4xl">Fulfillment Questions</h2>
          </div>
          <Accordion type="single" collapsible className="mt-10">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left text-base font-bold text-brand-black">{f.q}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-brand-gray">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </>
  );
}
